'use client';

import { c as _c } from "react/compiler-runtime";
import { type ItemsRenderProps, useAuth, useGetGiftsInfinite } from '@morfi/backend-sdk-v2/client';
import Image from 'next/image'; // ALLOW-RAW-IMG: static decorative placeholder, not user content
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { AllGiftsDialog } from '@/components/gift/all-gifts-dialog';
import { GiftButton } from '@/components/gift/gift-button';
import type { GiftRowData } from '@/components/gift/gift-row';
import { LiveCompactGiftRow } from '@/components/gift/live-compact-gift-row';
import { GiftsHowItWorksDialog } from '@/components/portfolio/gifts/gifts-how-it-works-dialog';
import { PortfolioPanelHeader } from '@/components/portfolio/shared/portfolio-panel-header';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/app-tabs';
import { Icon } from '@/components/ui/icon';
import { ExpandIcon } from '@/components/ui/icon/glyphs';
import { InfiniteList } from '@/components/ui/infinite-list';
import { Panel, PanelContent } from '@/components/ui/panel';
import { useGiftNotificationsRealtime } from '@/hooks/gifts/use-gift-notifications';
import { GIFT_LIST_FIELDS } from '@/lib/sdk/projections';
import { cn } from '@/lib/ui/utils';
type GiftsInboxSubtab = 'received' | 'sent';

/**
 * Compact gift row is fixed at 46px (avatar + two-line meta). Five rows
 * with the 4×8px (`gap-2`) gutters between them = 262px. The scroll
 * area is capped at exactly this height so 5 rows are visible on first
 * paint and overflow scrolls inside. The grid wrapping
 * `<GiftsSummaryCard>` + `<ReferralsPanel>` is `align-items: stretch` by
 * default — referrals stretches to whatever height the gifts panel
 * settles at.
 */
const SCROLL_AREA_HEIGHT_CLASS = 'h-[262px]' as const; // ARBITRARY: 5 × 46px gift rows + 4 × 8px gutters — see block comment above.

/** Canonical portfolio empty placeholder for both Received and Sent tabs. */
function GiftsSummaryEmptyVisual(t0) {
  const $ = _c(4);
  const {
    variant
  } = t0;
  const t = useTranslations("profile.next.summary.gifts");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Image src="/portfolio/gifts-placeholder.png" alt="" width={56} height={56} className="size-[56px]" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== t || $[2] !== variant) {
    t2 = <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-4 py-2 text-center">{t1}<div className="flex flex-col items-center gap-1.5">{variant === "sent" ? <><h3 className="text-heading-xs">{t("empty_title_sent")}</h3><p className="text-caption text-foreground-secondary">{t("empty_body_sent")}</p></> : <><h3 className="text-heading-xs">{t("empty_title")}</h3><p className="text-caption text-foreground-secondary">{t("empty_body")}</p></>}</div></div>;
    $[1] = t;
    $[2] = variant;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}

/**
 * Portfolio Gifts glance panel (spec 087).
 *
 * Fixed 320 px height (matches `<ReferralsSummaryCard>`), shows the 4 most
 * recent gifts in the active Sent/Received tab as compact rows. The footer
 * holds an Expand affordance (opens `<AllGiftsDialog>`, hidden when there
 * are no gifts) and the existing primary Send-gift CTA.
 */
export function GiftsSummaryCard() {
  const $ = _c(71);
  const t = useTranslations("profile.next.summary.gifts");
  const tInbox = useTranslations("gift.inbox");
  const tHowItWorks = useTranslations("gift.send.howItWorks");
  const [subTab, setSubTab] = useState("received");
  const [allGiftsOpen, setAllGiftsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  useGiftNotificationsRealtime();
  const {
    user
  } = useAuth();
  const userId = user?.id ?? "";
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [...GIFT_LIST_FIELDS];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== userId) {
    t1 = {
      userId,
      kind: "received",
      fields: t0
    };
    $[1] = userId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = userId !== "" && subTab === "received";
  let t3;
  if ($[3] !== t2) {
    t3 = {
      enabled: t2
    };
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const received = useGetGiftsInfinite(t1, t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [...GIFT_LIST_FIELDS];
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== userId) {
    t5 = {
      userId,
      kind: "sent",
      fields: t4
    };
    $[6] = userId;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = userId !== "" && subTab === "sent";
  let t7;
  if ($[8] !== t6) {
    t7 = {
      enabled: t6
    };
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const sent = useGetGiftsInfinite(t5, t7);
  const liveSource = subTab === "received" ? received : sent;
  const isLoading = userId === "" || liveSource.isPending;
  const isError = !isLoading && liveSource.isError;
  let t8;
  if ($[10] !== liveSource.rows) {
    t8 = liveSource.rows as ReadonlyArray<GiftRowData> | undefined ?? [];
    $[10] = liveSource.rows;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const sourceData = t8;
  const isEmptySuccess = !isLoading && !isError && sourceData.length === 0;
  const showExpand = !isEmptySuccess && !isError;
  const expandDisabled = isLoading;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = v => {
      if (v === "received" || v === "sent") {
        setSubTab(v);
      }
    };
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t) {
    t10 = t("title_label");
    $[13] = t;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== tHowItWorks) {
    t11 = tHowItWorks("title");
    $[15] = tHowItWorks;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setHelpOpen(true);
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== helpOpen || $[19] !== t11) {
    t13 = {
      label: t11,
      onClick: t12,
      expanded: helpOpen
    };
    $[18] = helpOpen;
    $[19] = t11;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== tInbox) {
    t14 = tInbox("tabs.received");
    $[21] = tInbox;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t14) {
    t15 = <TabsTrigger value="received">{t14}</TabsTrigger>;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== tInbox) {
    t16 = tInbox("tabs.sent");
    $[25] = tInbox;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== t16) {
    t17 = <TabsTrigger value="sent">{t16}</TabsTrigger>;
    $[27] = t16;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== t15 || $[30] !== t17) {
    t18 = <TabsList variant="segmented" className="w-auto">{t15}{t17}</TabsList>;
    $[29] = t15;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== t10 || $[33] !== t13 || $[34] !== t18) {
    t19 = <PortfolioPanelHeader title={t10} info={t13} action={t18} />;
    $[32] = t10;
    $[33] = t13;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  const t20 = `gifts-summary-list-${subTab}`;
  let t21;
  if ($[36] !== isEmptySuccess || $[37] !== isError || $[38] !== isLoading || $[39] !== liveSource || $[40] !== sourceData || $[41] !== subTab || $[42] !== tInbox) {
    t21 = isError ? <p className={cn("flex flex-col items-center justify-center rounded-control border border-feedback-error-border/30 bg-feedback-error-accent/5 p-4 text-body text-feedback-error-content", SCROLL_AREA_HEIGHT_CLASS)} data-testid="gifts-summary-error">{subTab === "received" ? tInbox("empty.received") : tInbox("empty.sent")}</p> : isLoading ? <output className={cn("flex flex-col gap-2 px-1", SCROLL_AREA_HEIGHT_CLASS)} aria-label={tInbox("loading")}><Skeleton className="h-[46px] w-full" /><Skeleton className="h-[46px] w-full" /><Skeleton className="h-[46px] w-full" /><Skeleton className="h-[46px] w-full" /><Skeleton className="h-[46px] w-full" /></output> : isEmptySuccess ? <div className={cn("flex", SCROLL_AREA_HEIGHT_CLASS)}><GiftsSummaryEmptyVisual variant={subTab} /></div> : <LiveGiftsList key={subTab} live={liveSource} subTab={subTab} sourceData={sourceData} heightClass={SCROLL_AREA_HEIGHT_CLASS} />;
    $[36] = isEmptySuccess;
    $[37] = isError;
    $[38] = isLoading;
    $[39] = liveSource;
    $[40] = sourceData;
    $[41] = subTab;
    $[42] = tInbox;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t20 || $[45] !== t21) {
    t22 = <div data-testid={t20}>{t21}</div>;
    $[44] = t20;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== subTab || $[48] !== t19 || $[49] !== t22) {
    t23 = <Tabs value={subTab} className="flex flex-col gap-3" onValueChange={t9}>{t19}{t22}</Tabs>;
    $[47] = subTab;
    $[48] = t19;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== allGiftsOpen || $[52] !== expandDisabled || $[53] !== showExpand || $[54] !== t) {
    t24 = showExpand ? <AnimatedButton type="button" variant="secondary" size="xl" disabled={expandDisabled} onClick={() => {
      setAllGiftsOpen(true);
    }} aria-label={t("expand_label")} aria-haspopup="dialog" aria-expanded={allGiftsOpen} data-testid="gifts-summary-expand"><Icon icon={ExpandIcon} /></AnimatedButton> : null;
    $[51] = allGiftsOpen;
    $[52] = expandDisabled;
    $[53] = showExpand;
    $[54] = t;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <GiftButton variant="primary" size="xl" className="flex-1" />;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== t24) {
    t26 = <div className="mt-auto flex shrink-0 items-center gap-2">{t24}{t25}</div>;
    $[57] = t24;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== t23 || $[60] !== t26) {
    t27 = <PanelContent className="flex flex-col gap-3">{t23}{t26}</PanelContent>;
    $[59] = t23;
    $[60] = t26;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== allGiftsOpen || $[63] !== subTab) {
    t28 = <AllGiftsDialog open={allGiftsOpen} onOpenChange={setAllGiftsOpen} activeTab={subTab} />;
    $[62] = allGiftsOpen;
    $[63] = subTab;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== helpOpen) {
    t29 = <GiftsHowItWorksDialog open={helpOpen} onOpenChange={setHelpOpen} />;
    $[65] = helpOpen;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== t27 || $[68] !== t28 || $[69] !== t29) {
    t30 = <Panel>{t27}{t28}{t29}</Panel>;
    $[67] = t27;
    $[68] = t28;
    $[69] = t29;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  return t30;
}
interface LiveGiftsListProps {
  live: ReturnType<typeof useGetGiftsInfinite>;
  subTab: GiftsInboxSubtab;
  sourceData: ReadonlyArray<GiftRowData>;
  heightClass: string;
}

/**
 * Wraps the `<live.Items>` render-prop + a snapshot lookup map so each
 * row's volatile `status` reads from `useGetGiftsField` (live) while
 * stable fields ride on the cached snapshot. The whole list re-renders
 * only on add/remove/reorder of gift ids; per-row patches flip in place.
 */
function LiveGiftsList(t0) {
  const $ = _c(14);
  const {
    live,
    subTab,
    sourceData,
    heightClass
  } = t0;
  let snapshotById;
  if ($[0] !== sourceData) {
    snapshotById = new Map();
    for (const row of sourceData) {
      snapshotById.set(row.giftPda ?? row.opaqueId, row);
    }
    $[0] = sourceData;
    $[1] = snapshotById;
  } else {
    snapshotById = $[1];
  }
  let t1;
  if ($[2] !== snapshotById || $[3] !== subTab) {
    t1 = t2 => {
      const {
        ref,
        id
      } = t2;
      const snapshot = snapshotById.get(id);
      return <div ref={ref}>{snapshot !== undefined && <LiveCompactGiftRow id={id} variant={subTab} snapshot={snapshot} />}</div>;
    };
    $[2] = snapshotById;
    $[3] = subTab;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== live.Items || $[6] !== t1) {
    t2 = <div className="flex flex-col gap-2 pr-1"><live.Items>{t1}</live.Items></div>;
    $[5] = live.Items;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== heightClass || $[9] !== live.fetchNextPage || $[10] !== live.hasNextPage || $[11] !== live.isFetchingNextPage || $[12] !== t2) {
    t3 = <InfiniteList hasNextPage={live.hasNextPage} isFetchingNextPage={live.isFetchingNextPage} fetchNextPage={live.fetchNextPage} maxHeight={262} className={heightClass} data-testid="gifts-summary-rows">{t2}</InfiniteList>;
    $[8] = heightClass;
    $[9] = live.fetchNextPage;
    $[10] = live.hasNextPage;
    $[11] = live.isFetchingNextPage;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}